/**
 * Barkod ve QR görüntü üretimi.
 *
 * Hem tasarımcı önizlemesi hem de yazdırma çıktısı aynı fonksiyonlardan geçer;
 * böylece ekranda görülen ile basılan aynı olur. Üretim canvas üzerinde yapılıp
 * data URL döner, Konva ve <img> ikisi de bunu doğrudan kullanabilir.
 */

import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { BarcodeFormat } from '../../../types/labelTemplate.types';

/** jsbarcode format adları bizim tiplerimizle bire bir eşleşmiyor */
const FORMAT_MAP: Record<BarcodeFormat, string> = {
  CODE128: 'CODE128',
  EAN13: 'EAN13',
  EAN8: 'EAN8',
  CODE39: 'CODE39',
  ITF14: 'ITF14',
};

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * JsBarcode EAN/ITF'de kontrol hanesi yanlışsa exception atar ve çizim boş kalır.
 * Stoktaki barkodlarda boşluk, yanlış check digit veya fazla hane sık görülüyor;
 * burada çizilebilir bir gövdeye indirgenir, check digit'i kütüphane ekler.
 */
export function normalizeBarcodeValue(value: string, format: BarcodeFormat): string | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;

  const takeBody = (digits: string, bodyLen: number, totalLen: number): string | null => {
    if (!digits) return null;
    let d = digits.length > totalLen ? digits.slice(-totalLen) : digits;
    if (d.length === totalLen) d = d.slice(0, bodyLen);
    if (d.length > bodyLen) d = d.slice(-bodyLen);
    if (d.length < bodyLen) d = d.padStart(bodyLen, '0');
    return d;
  };

  switch (format) {
    case 'EAN13':
      return takeBody(digitsOnly(raw), 12, 13);
    case 'EAN8':
      return takeBody(digitsOnly(raw), 7, 8);
    case 'ITF14':
      return takeBody(digitsOnly(raw), 13, 14);
    case 'CODE39':
      return raw.toUpperCase();
    default:
      return raw;
  }
}

/**
 * Barkod formatının veriyle uyumlu olup olmadığını kontrol eder.
 * EAN/ITF için rakam dışını yok sayar; kontrol hanesini biz düzelttiğimiz için
 * yanlış check digit uyarı değil.
 */
export function validateBarcodeValue(value: string, format: BarcodeFormat): string | null {
  const v = (value ?? '').trim();
  if (!v) return 'Barkod değeri boş';

  switch (format) {
    case 'EAN13': {
      const d = digitsOnly(v);
      if (d.length === 0) return 'EAN-13 için rakam gerekli';
      if (d.length < 8) return 'EAN-13 için en az 8 haneli sayı gerekli';
      return null;
    }
    case 'EAN8': {
      const d = digitsOnly(v);
      if (d.length === 0) return 'EAN-8 için rakam gerekli';
      if (d.length < 4) return 'EAN-8 için en az 4 haneli sayı gerekli';
      return null;
    }
    case 'ITF14': {
      const d = digitsOnly(v);
      if (d.length === 0) return 'ITF-14 için rakam gerekli';
      if (d.length < 8) return 'ITF-14 için en az 8 haneli sayı gerekli';
      return null;
    }
    case 'CODE39':
      if (!/^[0-9A-Z\-. $/+%]+$/i.test(v)) return 'CODE39 yalnızca harf, rakam ve - . $ / + % kabul eder';
      return null;
    case 'CODE128':
    default:
      return null;
  }
}

const moduleCountCache = new Map<string, number | null>();

/** Barkodun iki yanında kalması gereken boşluk, modül cinsinden (GS1: 10) */
const QUIET_ZONE_MODULES = 10;

/** Çubuklarla altındaki numara arasındaki boşluk (px) */
const TEXT_MARGIN_PX = 2;

/**
 * Barkodun kaç modül (en ince çubuk birimi) genişliğinde olduğunu ölçer.
 *
 * Modül sayısı yalnızca formata değil veriye de bağlı (CODE128 karakter setleri
 * arasında geçiş yaptıkça uzunluk değişir), bu yüzden hesaplamak yerine
 * kütüphaneye 1 px modülle çizdirip sonucun genişliğine bakılıyor.
 */
function countBarcodeModules(value: string, format: BarcodeFormat): number | null {
  const key = `${format}|${value}`;
  const cached = moduleCountCache.get(key);
  if (cached !== undefined) return cached;

  let modules: number | null = null;
  try {
    const probe = document.createElement('canvas');
    JsBarcode(probe, value, {
      format: FORMAT_MAP[format] ?? 'CODE128',
      width: 1,
      height: 10,
      displayValue: false,
      margin: 0,
    });
    modules = probe.width > 0 ? probe.width : null;
  } catch {
    modules = null;
  }

  if (moduleCountCache.size > MODULE_CACHE_LIMIT) moduleCountCache.clear();
  moduleCountCache.set(key, modules);
  return modules;
}

const MODULE_CACHE_LIMIT = 2000;

export interface FittedBarcodeOptions {
  format: BarcodeFormat;
  /** Barkodun sığacağı kutunun hedef çözünürlükteki genişliği (px/dot) */
  boxWidthPx: number;
  /** Kutu yüksekliği (px/dot) */
  boxHeightPx: number;
  showText?: boolean;
  color?: string;
}

/**
 * Barkodu hedef çözünürlükte, modül genişliği TAM SAYI dot olacak şekilde üretir.
 *
 * Neden gerekli: barkod sabit ölçüde üretilip kutuya esnetildiğinde çubuklar
 * kesirli dot'a denk geliyordu (ör. 1,53 dot). Ekranda bu yalnızca gri saçak
 * demek, ama termal baskıda görüntü 1-bit'e eşikleniyor ve saçaklar kalınlığı
 * çubuktan çubuğa kaydırıyor; okuyucu için barkodun ölçüsü bozuluyor.
 * Modül genişliği tam sayı dot olduğunda her çubuk aynı kalınlıkta basılıyor.
 *
 * Üretilen tuval kutudan dar olabilir (tam sayıya yuvarlama artığı); çağıran
 * taraf ortalamalı ve yatayda ölçeklememelidir.
 */
export function generateFittedBarcodeCanvas(
  value: string,
  opts: FittedBarcodeOptions,
): HTMLCanvasElement | null {
  const format = opts.format ?? 'CODE128';
  const v = normalizeBarcodeValue(value, format);
  if (!v) return null;

  const boxW = Math.floor(opts.boxWidthPx);
  const boxH = Math.floor(opts.boxHeightPx);
  if (boxW < 1 || boxH < 1) return null;

  const modules = countBarcodeModules(v, format);
  if (!modules) return null;

  // Sessiz bölge de kutunun içinde kalmalı; yoksa okuyucu barkodun nerede
  // başlayıp bittiğini ayırt edemiyor
  const totalModules = modules + QUIET_ZONE_MODULES * 2;
  let moduleWidth = Math.floor(boxW / totalModules);
  // Kutu bu barkoda dar kalıyorsa en ince çubukla üret; okunurluk düşer ama
  // esnetmeye göre yine de iyi
  if (moduleWidth < 1) moduleWidth = 1;

  const showText = opts.showText ?? true;
  const fontSize = showText ? Math.max(8, Math.round(boxH * 0.25)) : 0;
  const barHeight = Math.max(4, boxH - (showText ? fontSize + TEXT_MARGIN_PX : 0));

  try {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, v, {
      format: FORMAT_MAP[format] ?? 'CODE128',
      width: moduleWidth,
      height: barHeight,
      displayValue: showText,
      fontSize,
      textMargin: TEXT_MARGIN_PX,
      // Boşluk yalnızca yanlarda; dikeyde eklenirse çubuk boyu kısalır
      margin: 0,
      marginLeft: QUIET_ZONE_MODULES * moduleWidth,
      marginRight: QUIET_ZONE_MODULES * moduleWidth,
      lineColor: opts.color ?? '#000000',
      background: '#ffffff',
    });
    return canvas;
  } catch {
    return null;
  }
}

export interface QrRenderOptions {
  /** Kenar uzunluğu (px) */
  size?: number;
  errorLevel?: 'L' | 'M' | 'Q' | 'H';
  color?: string;
  margin?: number;
}

/** QR kodu canvas olarak üretir (senkron API kullanır) */
export function generateQrCanvas(value: string, opts?: QrRenderOptions): HTMLCanvasElement | null {
  const v = (value ?? '').trim();
  if (!v) return null;

  try {
    const canvas = document.createElement('canvas');
    QRCode.toCanvas(canvas, v, {
      width: opts?.size ?? 200,
      errorCorrectionLevel: opts?.errorLevel ?? 'M',
      margin: opts?.margin ?? 0,
      color: {
        dark: opts?.color ?? '#000000',
        light: '#ffffff',
      },
    });
    return canvas;
  } catch {
    return null;
  }
}

/** QR kodu data URL olarak üretir — <img> ve Konva için */
export function generateQrDataUrl(value: string, opts?: QrRenderOptions): string | null {
  return generateQrCanvas(value, opts)?.toDataURL('image/png') ?? null;
}

/**
 * Üretilen görüntüleri anahtara göre saklar.
 * Barkod üretimi her render'da tekrarlanırsa tasarımcı sürüklemede takılır.
 */
const cache = new Map<string, string | null>();
/**
 * Toplu baskıda her ürünün barkodu ayrı bir giriş. 100-200 ürünlük işler
 * olağan olduğu için sınır yüksek: sınır aşılınca önbellek tümden boşaldığı
 * (LRU değil) için düşük tutmak aynı barkodları baştan üretmeye yol açıyor.
 */
const CACHE_LIMIT = 2000;

export function getCachedQr(value: string, opts?: QrRenderOptions): string | null {
  const key = `q|${value}|${opts?.size}|${opts?.errorLevel}|${opts?.color}`;
  if (cache.has(key)) return cache.get(key) ?? null;
  const url = generateQrDataUrl(value, opts);
  if (cache.size > CACHE_LIMIT) cache.clear();
  cache.set(key, url);
  return url;
}

/**
 * Canvas önbelleği — doğrudan çizim yolu (labelCanvasRenderer) için.
 *
 * Data URL yolundan ayrı tutuluyor: canvas'ı PNG'ye kodlayıp tekrar çözmek
 * etiket başına onlarca milisaniye demek, oysa üretilen canvas drawImage ile
 * anında basılabiliyor. Piksel verisi bellekte durduğu için sınır daha düşük.
 */
const canvasCache = new Map<string, HTMLCanvasElement | null>();
const CANVAS_CACHE_LIMIT = 400;

function fittedKey(value: string, opts: FittedBarcodeOptions): string {
  return [
    'f',
    opts.format,
    value,
    Math.floor(opts.boxWidthPx),
    Math.floor(opts.boxHeightPx),
    opts.showText ?? true,
    opts.color ?? '',
  ].join('|');
}

export function getCachedFittedBarcodeCanvas(
  value: string,
  opts: FittedBarcodeOptions,
): HTMLCanvasElement | null {
  const key = fittedKey(value, opts);
  if (canvasCache.has(key)) return canvasCache.get(key) ?? null;
  const canvas = generateFittedBarcodeCanvas(value, opts);
  if (canvasCache.size > CANVAS_CACHE_LIMIT) canvasCache.clear();
  canvasCache.set(key, canvas);
  return canvas;
}

export interface FittedBarcodeImage {
  url: string;
  /** Üretilen görüntünün dot genişliği — kutudan dar olabilir */
  widthPx: number;
  heightPx: number;
}

const fittedImageCache = new Map<string, FittedBarcodeImage | null>();

/**
 * Ölçüye oturtulmuş barkodun data URL hali — <img> ve Konva için.
 *
 * Genişlik de dönüyor: görüntü kutudan dar olabildiği için çağıran taraf
 * kutuyu doldurmak yerine bu oranı korumalı, yoksa çubuklar yine esner.
 */
export function getCachedFittedBarcodeImage(
  value: string,
  opts: FittedBarcodeOptions,
): FittedBarcodeImage | null {
  const key = fittedKey(value, opts);
  const cached = fittedImageCache.get(key);
  if (cached !== undefined) return cached;

  const canvas = generateFittedBarcodeCanvas(value, opts);
  const result: FittedBarcodeImage | null = canvas
    ? { url: canvas.toDataURL('image/png'), widthPx: canvas.width, heightPx: canvas.height }
    : null;

  if (fittedImageCache.size > CACHE_LIMIT) fittedImageCache.clear();
  fittedImageCache.set(key, result);
  return result;
}

export function getCachedQrCanvas(
  value: string,
  opts?: QrRenderOptions,
): HTMLCanvasElement | null {
  const key = `q|${value}|${opts?.size}|${opts?.errorLevel}|${opts?.color}`;
  if (canvasCache.has(key)) return canvasCache.get(key) ?? null;
  const canvas = generateQrCanvas(value, opts);
  if (canvasCache.size > CANVAS_CACHE_LIMIT) canvasCache.clear();
  canvasCache.set(key, canvas);
  return canvas;
}
